import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from './config';
import AdminSidebar from './AdminSidebar';
import '../css/AdminManagement.css';
import '../css/SystemAdmin.css';

const ADMIN_RANKS = ['BG', 'BGM', 'NB', 'NB+', 'N-', 'N', 'N/S', 'S', 'S/P'];
const USER_MATCH_TYPES = [
  { value: 'PLAY_ALL_FAST', label: 'เล่นได้หมด เล่นไว', detail: 'แรงค์และคะแนนใกล้กัน เน้นไม่จัดคู่ซ้ำตั้งแต่ 2 รอบ' },
  { value: 'NO_CARRY', label: 'ไม่ตีคุ้ม', detail: 'แรงค์เท่ากัน เน้นคู่ที่คะแนนเท่ากันหรือสูงกว่า' },
  { value: 'TIGHT', label: 'ขอตึง', detail: 'แรงค์เท่ากันหรือใกล้กัน และอยู่ในโซนเดียวกัน' },
  { value: 'LIGHT', label: 'ขอเบา ๆ', detail: 'เลือกผู้เล่นแรงค์เท่ากันหรือต่ำกว่า' }
];

function SystemAdmin() {
  const navigate = useNavigate();
  const actor = useMemo(() => JSON.parse(localStorage.getItem('user') || 'null'), []);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const typeMode = new URLSearchParams(window.location.search).get('mode') === 'type';
  const selectedGroupId = Number(new URLSearchParams(window.location.search).get('groupId') || 0);

  const authFetch = useCallback(async (path, options = {}) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {})
      }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'ทำรายการไม่สำเร็จ');
    return data;
  }, []);

  const loadUsers = useCallback(async query => {
    if (typeMode && selectedGroupId <= 0) {
      setUsers([]);
      setError('กรุณาเลือกก๊วนก่อนกำหนด Type User');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const groupFilter = typeMode && selectedGroupId > 0
        ? `&groupId=${selectedGroupId}`
        : '';
      const data = await authFetch(`/api/admin/users?q=${encodeURIComponent(query || '')}${groupFilter}`);
      // Keep the newest account first even when an older backend/proxy returns
      // the legacy alphabetical order.
      setUsers([...(data.users || [])].sort(
        (left, right) => Number(right.user_id || 0) - Number(left.user_id || 0)
      ));
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, selectedGroupId, typeMode]);

  useEffect(() => {
    if (!actor || String(actor.role).toLowerCase() !== 'admin') {
      navigate('/', { replace: true });
      return undefined;
    }
    const timer = window.setTimeout(() => loadUsers(search), 300);
    return () => window.clearTimeout(timer);
  }, [actor, loadUsers, navigate, search]);

  const changePassword = async player => {
    const password = window.prompt(`Reset Password ให้ ${player.name}\nกรอกรหัสใหม่อย่างน้อย 4 ตัวอักษร`);
    if (password === null) return;
    if (password.length < 4) return alert('Password ต้องมีอย่างน้อย 4 ตัวอักษร');
    const confirmation = window.prompt('กรอก Password ใหม่อีกครั้งเพื่อยืนยัน');
    if (confirmation === null) return;
    if (password !== confirmation) return alert('Password ทั้งสองครั้งไม่ตรงกัน');
    try {
      setBusyId(player.user_id);
      const data = await authFetch(`/api/admin/users/${player.user_id}/password`, {
        method: 'PATCH', body: JSON.stringify({ password })
      });
      alert(data.message);
    } catch (changeError) {
      alert(changeError.message);
    } finally {
      setBusyId(null);
    }
  };

  const editName = async player => {
    const name = window.prompt(`แก้ไขชื่อผู้เล่น ID ${player.user_id}`, player.name || '');
    if (name === null) return;
    const normalizedName = name.trim();
    if (normalizedName.length < 2) return alert('ชื่อต้องมีอย่างน้อย 2 ตัวอักษร');
    if (normalizedName === String(player.name || '').trim()) return;
    try {
      setBusyId(player.user_id);
      const data = await authFetch(`/api/admin/users/${player.user_id}/name`, {
        method: 'PATCH', body: JSON.stringify({ name: normalizedName })
      });
      setUsers(current => current.map(item => (
        Number(item.user_id) === Number(player.user_id)
          ? { ...item, name: data.name || normalizedName }
          : item
      )));
      alert(data.message);
    } catch (changeError) {
      alert(changeError.message);
    } finally {
      setBusyId(null);
    }
  };

  const editRank = async (player, rank) => {
    if (!ADMIN_RANKS.includes(rank)) return;
    if (rank === String(player.level_rank || '').toUpperCase()) return;
    try {
      setBusyId(player.user_id);
      const data = await authFetch(`/api/admin/users/${player.user_id}/rank`, {
        method: 'PATCH', body: JSON.stringify({ rank })
      });
      setUsers(current => current.map(item => (
        Number(item.user_id) === Number(player.user_id)
          ? { ...item, level_rank: data.level_rank, point: data.point }
          : item
      )));
      alert(data.message);
    } catch (changeError) {
      alert(changeError.message);
    } finally {
      setBusyId(null);
    }
  };

  const editMatchType = async (player, matchType) => {
    if (!USER_MATCH_TYPES.some(item => item.value === matchType)) return;
    if (matchType === String(player.match_type || '')) return;
    try {
      setBusyId(player.user_id);
      const data = await authFetch(`/api/admin/users/${player.user_id}/match-type`, {
        method: 'PATCH', body: JSON.stringify({ match_type: matchType })
      });
      setUsers(current => current.map(item => (
        Number(item.user_id) === Number(player.user_id)
          ? { ...item, match_type: data.match_type }
          : item
      )));
    } catch (changeError) {
      alert(changeError.message);
    } finally {
      setBusyId(null);
    }
  };

  const deleteUser = async player => {
    if (!window.confirm(`ลบผู้เล่น “${player.name}” และข้อมูลที่เกี่ยวข้องทั้งหมดใช่หรือไม่?\nการลบไม่สามารถย้อนกลับได้`)) return;
    try {
      setBusyId(player.user_id);
      const data = await authFetch(`/api/admin/users/${player.user_id}`, { method: 'DELETE' });
      setUsers(current => current.filter(item => Number(item.user_id) !== Number(player.user_id)));
      alert(data.message);
    } catch (deleteError) {
      alert(deleteError.message);
    } finally {
      setBusyId(null);
    }
  };

  if (!actor || String(actor.role).toLowerCase() !== 'admin') return null;

  return (
    <main className="admin-management admin-shell-with-sidebar">
      <AdminSidebar active={typeMode ? 'user-types' : 'users'} />
      <header className="admin-management-header">
        <div>
          <button type="button" className="admin-back" onClick={() => navigate('/dashboard')}>← Dashboard</button>
          <h1>{typeMode ? '🏷️ กำหนด Type User' : '🛡 System Admin'}</h1>
          <p>{typeMode ? `แสดงเฉพาะสมาชิก Group #${selectedGroupId} · Type จะติดตาม User ไปทุกก๊วน` : 'ค้นหา แก้ไขชื่อ ระดับมือ Reset Password และลบผู้เล่นทั้งหมด'}</p>
        </div>
      </header>
      <section className="admin-search-panel">
        <label htmlFor="admin-user-search">ค้นหาผู้เล่น</label>
        <input id="admin-user-search" value={search} onChange={event => setSearch(event.target.value)} placeholder="พิมพ์ชื่อหรือ Player ID..." autoFocus />
        <span>พบ {users.length} คน</span>
      </section>
      {error && <div className="admin-message error">{error}</div>}
      {loading ? <div className="admin-empty">กำลังค้นหา...</div> : users.length === 0 ? <div className="admin-empty">ไม่พบผู้เล่น</div> : (
        <section className="admin-user-list">
          {users.map(player => {
            const isAdmin = String(player.role || '').toLowerCase() === 'admin';
            const isBusy = Number(busyId) === Number(player.user_id);
            return (
              <article className="admin-user-card" key={player.user_id}>
                <div className="admin-user-avatar">{String(player.name || '?').slice(0, 1)}</div>
                <div className="admin-user-info">
                  <strong>{player.name}</strong>
                  <span>ID {player.user_id} • {player.level_rank || '-'} • {Number(player.point || 0)} คะแนน • {player.group_count} Group</span>
                  {isAdmin && <small className="admin-role">ADMIN</small>}
                  {typeMode && <small>{USER_MATCH_TYPES.find(item => item.value === player.match_type)?.detail || 'ยังไม่ได้กำหนด Type — ใช้เงื่อนไข Pick One เดิม'}</small>}
                </div>
                <div className="admin-user-actions">
                  {typeMode ? (
                    <label className="system-admin-rank-select">
                      <span>🏷️ Type ผู้เล่น</span>
                      <select
                        value={player.match_type || ''}
                        disabled={isBusy}
                        onChange={event => editMatchType(player, event.target.value)}
                        aria-label={`กำหนด Type ของ ${player.name}`}
                      >
                        <option value="" disabled>เลือก Type</option>
                        {USER_MATCH_TYPES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                      </select>
                    </label>
                  ) : <>
                  <button type="button" disabled={isBusy} onClick={() => editName(player)}>✏️ แก้ไขชื่อ</button>
                  <label className="system-admin-rank-select">
                    <span>🏸 ระดับมือ</span>
                    <select
                      value={player.level_rank || 'BG'}
                      disabled={isBusy}
                      onChange={event => editRank(player, event.target.value)}
                      aria-label={`เลือกระดับมือของ ${player.name}`}
                    >
                      {ADMIN_RANKS.map(rank => <option key={rank} value={rank}>{rank}</option>)}
                    </select>
                  </label>
                  <button type="button" disabled={isBusy} onClick={() => changePassword(player)}>🔑 Reset Password</button>
                  <button type="button" className="danger" disabled={isBusy || isAdmin || Number(actor.user_id) === Number(player.user_id)} onClick={() => deleteUser(player)}>🗑 ลบผู้เล่น</button>
                  </>}
                </div>
              </article>
            );
          })}
        </section>
      )}
    </main>
  );
}

export default SystemAdmin;
